import cloneDeep from 'lodash/cloneDeep';
import React, { useEffect, useState } from 'react';
import {
    defaultLightEnhance,
    type ImageEnhancePlugin,
} from '@meitu/saas-editor-core/es/layers/main-layer/plugins/apps/user-editor/image-enhance';
import { useBatchActionHooks } from '../batch-action-hooks';
import EnhanceSlider from '../enhance-slider';
import Reset from '../reset';
import { IEnhanceFeatureProps, IEnhanceSlider } from '../types';
import { getSliderConfig } from './config';

export const LightFeature: React.FC<IEnhanceFeatureProps> = ({
    editor,
    theme,
    onReset,
    onBeforeEnhance,
    onEnhance,
    onAfterEnhance,
    trackFunc,
    isBatchEditor,
    setStageLoading,
    batchSetIsLoading,
    setIsDisable,
}) => {
    const { batchAction } = useBatchActionHooks();
    const [enhanceState, _setEnhanceState] = useState(
        editor.getInstance<ImageEnhancePlugin>('ImageEnhancePlugin').getState(),
    );

    useEffect(() => {
        return editor.getInstance<ImageEnhancePlugin>('ImageEnhancePlugin').watch((state) => _setEnhanceState(state));
    }, [editor]);

    const { lightEnhance } = enhanceState;

    const [sliderSkin, setSliderSkin] = useState<IEnhanceSlider[]>([]);

    useEffect(() => {
        setSliderSkin(getSliderConfig(theme));
    }, [theme]);

    return (
        <>
            <div>
                {sliderSkin.map((v, i) => {
                    return (
                        <EnhanceSlider
                            key={i}
                            maxVal={v.maxVal}
                            minVal={v.minVal}
                            value={lightEnhance[v.featureType][v.keyName]}
                            title={v.name}
                            opacityOpposite={v.opacityOpposite}
                            onChange={async (val: number) => {
                                const newParams = cloneDeep(lightEnhance);

                                newParams[v.featureType][v.keyName] = val;
                                editor.getInstance<ImageEnhancePlugin>('ImageEnhancePlugin').setLightEnhance(newParams);
                                onBeforeEnhance?.(v.keyName, val);
                                await editor
                                    .getInstance<ImageEnhancePlugin>('ImageEnhancePlugin')
                                    .basicEnhance.action(newParams, trackFunc);
                                onEnhance?.(v.keyName, val);
                            }}
                            onAfterChange={async (val) => {
                                if (isBatchEditor) {
                                    const newParams = cloneDeep(lightEnhance);

                                    newParams[v.featureType][v.keyName] = val;

                                    batchAction(
                                        editor,
                                        newParams,
                                        (stageIndex: number, isBatch: boolean) => {
                                            setStageLoading?.(stageIndex, isBatch);
                                        },
                                        setIsDisable,
                                        batchSetIsLoading,
                                    );
                                }
                                onAfterEnhance?.(v.keyName, val);
                            }}
                            step={v.step}
                            marks={v.marks}
                            background={v.background}
                            wrapBackground={v.wrapBackground}
                        />
                    );
                })}
            </div>
            <Reset
                onClick={async () => {
                    const newParams = cloneDeep(defaultLightEnhance);

                    editor.getInstance<ImageEnhancePlugin>('ImageEnhancePlugin').setLightEnhance(newParams);
                    if (isBatchEditor) {
                        setIsDisable?.(true);
                        await editor.getInstance<ImageEnhancePlugin>('ImageEnhancePlugin').basicEnhance.batchReset();
                        setIsDisable?.(false);
                    } else {
                        editor.getInstance<ImageEnhancePlugin>('ImageEnhancePlugin').basicEnhance.action(newParams);
                    }
                    onReset?.();
                }}
            />
        </>
    );
};
